import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';
import { sceneFirstVersionBody, type LegacyScene } from './scenes';

// scene_origins table; StoryDto.From in lureo-backend Data/Api/V1/Scenes/ScenesDtos.cs
export type StoryDto = components['schemas']['StoryDto'];
export type CreateStoryRequest = components['schemas']['CreateStoryRequest'];
export type StoryVisibility = components['schemas']['StoryVisibility'];
/** What the creator picks; only Lureo takes a story down. */
export type ChosenVisibility = Exclude<StoryVisibility, 'takenDown'>;

const spec = {
  uniqueId: 'scene_origin_id',
  projectUniqueId: 'project_instance_id',
  userUniqueId: 'user_id',
  name: 'scene_origin_name',
  description: 'scene_origin_description',
  orderNumber: 'scene_origin_order_number',
  coverImgUrl: 'scene_origin_cover_img_url',
  // A null column reads as false
  userHasUploadedCoverImage: 'scene_origin_user_has_uploaded_cover_image',
  // Only a JSON object survives; any other stored JSON reads as null
  locationInfo: { legacy: 'scene_origin_location_info', as: 'jsonText' },
  category: 'scene_origin_category',
  tags: 'scene_origin_tags',
  difficulty: 'scene_origin_difficulty',
  learnings: 'scene_origin_learnings',
  features: 'scene_origin_features',
  suppressedAt: 'suppressed_at',
  suppressedReason: 'suppressed_reason',
  visibility: 'visibility',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<StoryDto>;

const map = fieldMap<StoryDto, typeof spec>('Story', spec);

export type LegacyStory = LegacyRow<typeof spec, StoryDto>;

/** updateStory accepts these. */
export const STORY_PATCH_FIELDS = [
  'name', 'description', 'coverImgUrl', 'locationInfo', 'orderNumber', 'category', 'tags', 'difficulty', 'learnings', 'features',
] as const;

export const storyToLegacy = (dto: StoryDto): LegacyStory => map.toLegacy(dto);

/**
 * createStory body from the legacy scene_origins and first scene_instances insert rows, which it
 * replaces in one transaction. scene_origin_id and project_instance_id are required. The server
 * numbers the story after the project's last one and recounts the project, so the order number,
 * owner and timestamps are skipped. Other story columns are refused: set them with updateStory.
 */
export function storyCreateBody(story: Partial<LegacyStory>, firstScene: Partial<LegacyScene>): CreateStoryRequest {
  const body = map.toBody(story, ['uniqueId', 'projectUniqueId', 'name'], ['user_id', 'scene_origin_order_number', 'created_at', 'updated_at']);
  return {
    ...body,
    uniqueId: requireId('Story', 'scene_origin_id', body.uniqueId),
    projectUniqueId: requireId('Story', 'project_instance_id', body.projectUniqueId),
    firstScene: sceneFirstVersionBody(firstScene),
  };
}

/** updateStory body from a legacy partial update; updated_at is stamped by the server. */
export const storyPatchBody = (changes: Partial<LegacyStory>) => map.toBody(changes, STORY_PATCH_FIELDS, ['updated_at']);
